import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    X, Loader2, CheckCircle2, Building2, User, AlertTriangle, Undo2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
    DiffTable, ImportResultLinks, PersonRow, VerdictBadge,
    pbValue, defaultDecision, willWrite,
} from './ImportDiffParts';
import { isEmpty } from '../../utils/pitchbookDiff';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ownerRecordsFromPreview(preview, {
    includeCompany = true,
    selectedPersons = null,
    personFocusId = null,
    companyOnly = false,
} = {}) {
    const records = [];
    const companyOwner = preview?.company?.matched_crm_account;
    if (includeCompany && companyOwner?.owner_id) {
        records.push({
            id: companyOwner.owner_id,
            name: companyOwner.owner_name,
            recordName: preview?.company?.name || companyOwner.name || 'company',
        });
    }

    if (!companyOnly) {
        for (const person of preview?.persons || []) {
            const selected = selectedPersons
                ? !!selectedPersons[person.pitchbook_id]
                : (personFocusId ? person.pitchbook_id === personFocusId : true);
            const personOwner = person.matched_crm_contact;
            if (selected && personOwner?.owner_id) {
                records.push({
                    id: personOwner.owner_id,
                    name: personOwner.owner_name,
                    recordName: person.name || personOwner.name || 'person',
                });
            }
        }
    }

    return records;
}

function uniqueOwners(ownerRecords) {
    const owners = new Map();
    for (const owner of ownerRecords) {
        if (!owners.has(owner.id)) owners.set(owner.id, owner);
    }
    return [...owners.values()];
}

function defaultOwnerFromPreview(preview, { personFocusId = null, companyOnly = false } = {}) {
    const owners = uniqueOwners(ownerRecordsFromPreview(preview, { personFocusId, companyOnly }));
    return owners.length === 1 ? owners[0] : { id: '', name: '' };
}

function userLabel(user) {
    if (!user) return '';
    return `${user.name || user.email || 'Unnamed user'}${user.email ? ` (${user.email})` : ''}`;
}

// ─── Main dialog ──────────────────────────────────────────────────────────────

/**
 * @param {string}  pitchbookId       PitchBook company/investor ID to preview
 * @param {string}  [recordName]      Display name shown in the header
 * @param {string}  [personFocusId]   Pre-expand and pre-select one person
 * @param {string}  [targetAccountId] When set, force-links the import to this
 *                                    CRM account (bypasses match ladder). Used
 *                                    by the "Update from PitchBook" flow on the
 *                                    Company detail page.
 * @param {boolean} [companyOnly]     When true, hide the People section. Used
 *                                    by the "Update from PitchBook" flow.
 * @param {Array}   [orgUsers]        Org users available for owner assignment.
 * @param {function} onClose
 * @param {function} [onCommitted]
 */
export default function ImportDiffDialog({
    pitchbookId,
    recordName,
    personFocusId = null,
    targetAccountId = null,
    companyOnly = false,
    orgUsers = [],
    onClose,
    onCommitted,
}) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [committing, setCommitting] = useState(false);
    const [result, setResult] = useState(null);
    const [undoing, setUndoing] = useState(false);
    const [undone, setUndone] = useState(false);

    const [includeCompany, setIncludeCompany] = useState(true);
    const [selectedPersons, setSelectedPersons] = useState({});
    const [expandedPersons, setExpandedPersons] = useState({});
    // {company: {col: policy}, persons: {pbId: {col: policy}}} — only deviations.
    const [companyDecisions, setCompanyDecisions] = useState({});
    const [personDecisions, setPersonDecisions] = useState({});
    const [selectedOwnerId, setSelectedOwnerId] = useState('');

    const loadPreview = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(`${API}/pitchbook/manual-sync/preview`,
                {
                    pitchbook_id: pitchbookId,
                    ...(targetAccountId ? { target_account_id: targetAccountId } : {}),
                },
                { headers: getAuthHeader() });
            setPreview(res.data);
            setIncludeCompany(true);
            setSelectedOwnerId(defaultOwnerFromPreview(res.data, { personFocusId, companyOnly }).id || '');
            // Focused on one person → select just them; otherwise default to
            // everyone (an explicit [] tells the backend to import no people).
            setSelectedPersons(Object.fromEntries(
                (res.data.persons || []).map((p) => [
                    p.pitchbook_id,
                    personFocusId ? p.pitchbook_id === personFocusId : true,
                ])
            ));
            if (personFocusId) setExpandedPersons({ [personFocusId]: true });
        } catch (e) {
            const status = e.response?.status;
            if (status === 422) setError(e.response?.data?.detail || 'This record is too large to import in one go.');
            else if (status === 404) setError('This record was not found in the indexed PitchBook data.');
            else if (status === 409) setError(e.response?.data?.detail?.drift_report
                ? 'The feed schema changed — resolve the drift before importing.'
                : (e.response?.data?.detail || 'No indexed PitchBook data yet.'));
            else setError('Failed to load the import preview.');
        } finally {
            setLoading(false);
        }
    }, [pitchbookId, personFocusId, targetAccountId, companyOnly, getAuthHeader]);

    useEffect(() => { loadPreview(); }, [loadPreview]);

    const setCompanyDecision = (col, policy) =>
        setCompanyDecisions((s) => ({ ...s, [col]: policy }));
    const setPersonDecision = (pbId, col, policy) =>
        setPersonDecisions((s) => ({ ...s, [pbId]: { ...(s[pbId] || {}), [col]: policy } }));

    // Only ship decisions that deviate from the saved mapping policy.
    const buildOverrides = useCallback(() => {
        const overrides = { company: {}, persons: {} };
        const fieldsByCol = Object.fromEntries((preview?.company?.fields || []).map((f) => [f.csv_column, f]));
        for (const [col, policy] of Object.entries(companyDecisions)) {
            const row = fieldsByCol[col];
            if (row && policy !== defaultDecision(row)) overrides.company[col] = policy;
        }
        for (const p of preview?.persons || []) {
            const personCols = personDecisions[p.pitchbook_id] || {};
            const byCol = Object.fromEntries((p.fields || []).map((f) => [f.csv_column, f]));
            const out = {};
            for (const [col, policy] of Object.entries(personCols)) {
                const row = byCol[col];
                if (row && policy !== defaultDecision(row)) out[col] = policy;
            }
            if (Object.keys(out).length) overrides.persons[p.pitchbook_id] = out;
        }
        return overrides;
    }, [preview, companyDecisions, personDecisions]);

    const summary = useMemo(() => {
        if (!preview) return { writes: 0, kept: 0 };
        let writes = 0;
        let kept = 0;
        const tally = (fields, decisions, active) => {
            if (!active) return;
            for (const f of fields || []) {
                if (!f.target || f.policy === 'pitchbook_tab_only' || isEmpty(pbValue(f))) continue;
                const d = decisions[f.csv_column] ?? defaultDecision(f);
                if (willWrite(f, d)) writes += 1;
                else kept += 1;
            }
        };
        tally(preview.company?.fields, companyDecisions, includeCompany);
        for (const p of preview.persons || []) {
            tally(p.fields, personDecisions[p.pitchbook_id] || {}, !!selectedPersons[p.pitchbook_id]);
        }
        return { writes, kept };
    }, [preview, companyDecisions, personDecisions, includeCompany, selectedPersons]);

    const runCommit = async () => {
        setCommitting(true);
        try {
            const res = await axios.post(`${API}/pitchbook/manual-sync/commit`, {
                preview_id: preview.preview_id,
                include_company: includeCompany,
                selected_person_ids: Object.entries(selectedPersons)
                    .filter(([, v]) => v).map(([pid]) => pid),
                field_overrides: buildOverrides(),
                owner_id: selectedOwnerId,
            }, { headers: getAuthHeader() });
            setResult(res.data);
            onCommitted?.();
        } catch (e) {
            if (e.response?.status === 409) {
                toast.error(e.response?.data?.detail || 'Preview stale — reloading');
                loadPreview();
            } else if (e.response?.status === 422) {
                toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Invalid import selection');
            } else {
                toast.error('Import failed');
            }
        } finally {
            setCommitting(false);
        }
    };

    const runUndo = async () => {
        if (!result?.run_id) return;
        setUndoing(true);
        try {
            await axios.post(`${API}/pitchbook/manual-sync/runs/${result.run_id}/undo`,
                {}, { headers: getAuthHeader() });
            setUndone(true);
            toast.success('Import reverted');
            onCommitted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to revert');
        } finally {
            setUndoing(false);
        }
    };

    const selectedPersonCount = Object.values(selectedPersons).filter(Boolean).length;
    const canCommit = (includeCompany || selectedPersonCount > 0) && !!selectedOwnerId;
    const company = preview?.company;
    const isUpdate = company?.verdict === 'update' || company?.verdict === 'skip_no_change';
    const stats = result?.projection_stats;
    const selectedOwner = orgUsers.find((u) => u.user_id === selectedOwnerId);
    const existingOwners = uniqueOwners(ownerRecordsFromPreview(preview, {
        includeCompany,
        selectedPersons,
        personFocusId,
        companyOnly,
    }));
    const ownerChanges = ownerRecordsFromPreview(preview, {
        includeCompany,
        selectedPersons,
        personFocusId,
        companyOnly,
    }).filter((owner) => selectedOwnerId && owner.id !== selectedOwnerId);
    const currentOwnerNames = uniqueOwners(ownerChanges)
        .map((owner) => owner.name || owner.id)
        .join(', ');

    const setAllPersons = (selected) =>
        setSelectedPersons(Object.fromEntries((preview?.persons || []).map((p) => [p.pitchbook_id, selected])));

    return createPortal((
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-stone-200">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Import to CRM — {recordName || pitchbookId}
                        </h3>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Review the side-by-side diff, choose what each field should do, then import.
                            Nothing is written until you confirm.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-700 shrink-0 ml-3"><X className="w-5 h-5" /></button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-5">
                    {loading && (
                        <div className="py-16 text-center text-stone-400">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                            <p className="text-xs">Building the preview…</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-800">{error}</p>
                        </div>
                    )}

                    {!loading && !error && preview && !result && (
                        <div className="space-y-5">
                            <div className="rounded-lg border border-stone-200 bg-stone-50/60 px-4 py-3">
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                    <div>
                                        <p className="text-sm font-semibold text-stone-800">Owner</p>
                                        <p className="text-xs text-stone-500">
                                            Choose the CRM owner for the imported company and people.
                                        </p>
                                    </div>
                                    <div className="w-full sm:w-72">
                                        <Select value={selectedOwnerId || undefined} onValueChange={setSelectedOwnerId}>
                                            <SelectTrigger className="bg-white">
                                                <SelectValue placeholder="Select an owner..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {orgUsers.map((rep) => (
                                                    <SelectItem key={rep.user_id} value={rep.user_id}>
                                                        {userLabel(rep)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {ownerChanges.length > 0 && (
                                    <div className="mt-3 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                                        {ownerChanges.length === 1
                                            ? <>Owner will be changed from {ownerChanges[0].name || 'the current owner'} to {userLabel(selectedOwner) || 'the selected owner'}.</>
                                            : <>Owner will be changed on {ownerChanges.length} existing records to {userLabel(selectedOwner) || 'the selected owner'}. Current owners: {currentOwnerNames || 'multiple owners'}.</>}
                                    </div>
                                )}
                                {!selectedOwnerId && existingOwners.length > 1 && (
                                    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                        Selected records currently have different owners. Choose the owner to apply to all imported records.
                                    </div>
                                )}
                                {orgUsers.length === 0 && (
                                    <p className="mt-2 text-xs text-rose-700">No org users are available for owner assignment. Refresh and try again.</p>
                                )}
                                {!selectedOwnerId && (
                                    <p className="mt-2 text-xs text-amber-700">Select an owner before confirming the import.</p>
                                )}
                            </div>

                            {/* Company block */}
                            <div>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <label className="flex items-center gap-2 min-w-0">
                                        <input
                                            type="checkbox"
                                            checked={includeCompany}
                                            onChange={(e) => setIncludeCompany(e.target.checked)}
                                            className="rounded border-stone-300 text-stone-900 focus:ring-stone-700"
                                        />
                                        <Building2 className="w-4 h-4 text-stone-500 shrink-0" />
                                        <h4 className="font-semibold text-stone-800 text-sm truncate">
                                            {company.entity === 'investor' ? 'Investor' : 'Company'}
                                        </h4>
                                        <VerdictBadge verdict={company.verdict} />
                                    </label>
                                    {company.matched_crm_account && (
                                        <span className="text-xs text-stone-500 truncate">→ {company.matched_crm_account.name}</span>
                                    )}
                                </div>
                                {includeCompany && (
                                    <DiffTable
                                        fields={company.fields}
                                        decisions={companyDecisions}
                                        onDecision={setCompanyDecision}
                                        isUpdate={isUpdate}
                                    />
                                )}
                            </div>

                            {preview.warnings?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    {preview.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                </div>
                            )}

                            {/* People (hidden in company-only mode) */}
                            {!companyOnly && preview.persons?.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-stone-500" />
                                            <h4 className="font-semibold text-stone-800 text-sm">
                                                People ({selectedPersonCount}/{preview.persons.length} selected)
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs">
                                            <button type="button" onClick={() => setAllPersons(true)} className="text-stone-600 hover:text-stone-900">Select all</button>
                                            <span className="text-stone-300">·</span>
                                            <button type="button" onClick={() => setAllPersons(false)} className="text-stone-600 hover:text-stone-900">Select none</button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {preview.persons.map((p) => (
                                            <PersonRow
                                                key={p.pitchbook_id}
                                                person={p}
                                                selected={!!selectedPersons[p.pitchbook_id]}
                                                expanded={!!expandedPersons[p.pitchbook_id]}
                                                onToggleSelect={(v) => setSelectedPersons((s) => ({ ...s, [p.pitchbook_id]: v }))}
                                                onToggleExpand={() => setExpandedPersons((s) => ({ ...s, [p.pitchbook_id]: !s[p.pitchbook_id] }))}
                                                decisions={personDecisions[p.pitchbook_id] || {}}
                                                onDecision={(col, policy) => setPersonDecision(p.pitchbook_id, col, policy)}
                                                highlighted={p.pitchbook_id === personFocusId}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!includeCompany && selectedPersonCount > 0 && company.verdict === 'create' && (
                                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>This company is not in the CRM yet — selected people will be imported without a company link.</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Result */}
                    {result && (
                        <div className="text-center py-8 space-y-4">
                            {undone ? (
                                <>
                                    <Undo2 className="w-12 h-12 text-stone-400 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import reverted</h4>
                                    <p className="text-sm text-stone-500">All changes from this import were rolled back.</p>
                                </>
                            ) : (
                                <>
                                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
                                    <h4 className="font-semibold text-stone-800">Import completed</h4>
                                    <div className="inline-flex flex-col gap-1 text-sm text-stone-600">
                                        <span>Companies: {stats?.company?.created || 0} created · {stats?.company?.updated || 0} updated · {stats?.company?.skipped || 0} unchanged</span>
                                        <span>People: {stats?.person?.created || 0} created · {stats?.person?.updated || 0} updated · {stats?.person?.skipped || 0} unchanged</span>
                                    </div>
                                    {result.warnings?.length > 0 && (
                                        <div className="max-w-xl mx-auto bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 text-left">
                                            {result.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                                        </div>
                                    )}
                                    <ImportResultLinks links={result.links} />
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-4 border-t border-stone-200">
                    <div className="text-xs text-stone-500">
                        {!loading && !error && preview && !result && (
                            <span>
                                Will write <strong className="text-stone-800">{summary.writes}</strong> field{summary.writes === 1 ? '' : 's'}
                                {summary.kept > 0 && <> · keep <strong className="text-stone-700">{summary.kept}</strong> existing</>}
                            </span>
                        )}
                        {result && !undone && (
                            <button onClick={runUndo} disabled={undoing}
                                className="flex items-center gap-1.5 text-stone-500 hover:text-rose-600">
                                {undoing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                                Undo this import
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {result ? (
                            <Button onClick={onClose}>Done</Button>
                        ) : (
                            <>
                                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                                <Button
                                    onClick={runCommit}
                                    disabled={committing || loading || !!error || !canCommit}
                                    title={!selectedOwnerId ? 'Select an owner before importing' : undefined}
                                >
                                    {committing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                                    Import {includeCompany ? '1 record' : '0 records'} + {selectedPersonCount} {selectedPersonCount === 1 ? 'person' : 'people'}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    ), document.body);
}
